class DbtfySliderComponent extends HTMLElement {
  constructor() {
    super();
    this.onScroll = this.update.bind(this);
    this.onPrevious = this.goToPreviousSlide.bind(this);
    this.onNext = this.goToNextSlide.bind(this);
    this.loadDeferredMedia = this.loadDeferredMedia.bind(this);
    this.afterFirstImage = () => {
      // Let the first image paint before starting the hidden slides' downloads.
      this.mediaFrame = requestAnimationFrame(() => {
        this.mediaFrame = requestAnimationFrame(this.loadDeferredMedia);
      });
    };
    this.onDotClick = (event) => {
      const button = event.target.closest('[data-slide]');
      if (button) this.goToSlide(Number(button.dataset.slide));
    };
  }

  connectedCallback() {
    this.slider = this.querySelector('[id^="Slider-"]');
    if (!this.slider || this.visibilityObserver) return;
    this.currentPage = 1;
    this.totalPages = 0;
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    if (this.querySelector('template[data-slider-media]')) {
      this.firstImage = this.sliderItems[0]?.querySelector('picture img');
      this.slider.addEventListener('pointerdown', this.loadDeferredMedia, { passive: true });
      this.slider.addEventListener('keydown', this.loadDeferredMedia);
      if (!this.firstImage || this.firstImage.complete) this.afterFirstImage();
      else {
        this.firstImage.addEventListener('load', this.afterFirstImage, { once: true });
        this.firstImage.addEventListener('error', this.afterFirstImage, { once: true });
      }
    }
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.autoPlay = this.dataset.autoplay === 'true';
    this.autoplaySpeed = 1000 * this.dataset.speed || 5000;
    this.loop = this.dataset.loop === 'true';
    this.arrows = this.dataset.arrows === 'true';
    this.dots = this.dataset.dots === 'true';
    this.controlsPosition = this.dataset.controlsPosition || 'bottom';
    this.controlsClass = this.dataset.controlsClass || 'slider-buttons';
    // Native scrolling and server-rendered images work before enhancement.
    // Avoid measuring every off-screen product carousel during the hero paint.
    if ('IntersectionObserver' in window) {
      this.visibilityObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.visibilityObserver.disconnect();
          this.start();
        }
      }, { rootMargin: '200px' });
      this.visibilityObserver.observe(this);
    } else {
      this.start();
    }
  }

  start() {
    if (this.started || !this.isConnected) return;
    this.started = true;
    this.slider.addEventListener('scroll', this.onScroll, { passive: true });
    // ResizeObserver delivers the first size without a synchronous layout read.
    this.resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      if (!width || width === this.lastWidth) return;
      this.lastWidth = width;
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => this.refresh());
    });
    this.resizeObserver.observe(this.slider);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.mediaFrame);
    this.firstImage?.removeEventListener('load', this.afterFirstImage);
    this.firstImage?.removeEventListener('error', this.afterFirstImage);
    this.slider?.removeEventListener('pointerdown', this.loadDeferredMedia);
    this.slider?.removeEventListener('keydown', this.loadDeferredMedia);
    this.visibilityObserver?.disconnect();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.resizeFrame);
    clearInterval(this.autoplay);
    this.slider?.removeEventListener('scroll', this.onScroll);
    this.prevButton?.removeEventListener('click', this.onPrevious);
    this.nextButton?.removeEventListener('click', this.onNext);
    this.dotsWrapper?.removeEventListener('click', this.onDotClick);
    this.visibilityObserver = null;
    this.started = false;
    this.lastWidth = null;
    this.dotCount = null;
  }

  refresh() {
    this.initPages();
    if (this.arrows && this.totalPages > 1) this.initializeArrows();
    if (this.dots && this.totalPages > 1) this.initializeDots();
    if (this.controlsPosition === 'inside') {
      const controls = this.querySelector(`.${this.controlsClass}`);
      const buttons = this.querySelector('.slider-buttons');
      if (controls && buttons) {
        const height = buttons.offsetHeight;
        const margin = getComputedStyle(this.slider).marginBottom;
        const item = this.querySelector('.grid__item');
        const padding = item ? getComputedStyle(item).paddingBottom : '0px';
        if (!controls.classList.contains('slideshow__controls--top')) controls.style.position = 'relative';
        controls.style.marginTop = `calc(0px - (${height}px + ${margin} + ${padding}))`;
      }
    }
    this.update();
    clearInterval(this.autoplay);
    if (this.autoPlay && this.totalPages > 1) this.play();
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((item) => item.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) {
      this.totalPages = this.sliderItemsToShow.length;
      return;
    }
    const firstOffset = this.sliderItemsToShow[0].offsetLeft;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - firstOffset;
    if (this.sliderItemOffset <= 0) return;
    this.slidesPerPage = Math.floor((this.slider.clientWidth - firstOffset) / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + (firstOffset > 0 ? 1 : 0);
  }

  update() {
    if (!this.sliderItemOffset) return;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;
    if (this.dots) this.handleDotState();
    if (this.arrows && !this.loop && this.prevButton && this.nextButton) {
      this.prevButton.disabled = this.currentPage === 1;
      this.nextButton.disabled = this.currentPage === this.totalPages;
    }
  }

  initializeDots() {
    const wrapper = this.querySelector('.slideshow__control-wrapper');
    if (!wrapper) return;
    if (this.dotsWrapper !== wrapper) this.dotsWrapper?.removeEventListener('click', this.onDotClick);
    this.dotsWrapper = wrapper;
    wrapper.addEventListener('click', this.onDotClick);
    if (this.dotCount === this.totalPages) return;
    const fragment = document.createDocumentFragment();
    for (let page = 1; page <= this.totalPages; page++) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'slider-counter__link slider-counter__link--dots link';
      button.dataset.slide = page;
      button.setAttribute('aria-label', `Slide ${page}`);
      const dot = document.createElement('span');
      dot.className = 'dot';
      button.appendChild(dot);
      fragment.appendChild(button);
    }
    wrapper.replaceChildren(fragment);
    wrapper.parentElement.classList.remove('hidden');
    this.dotCount = this.totalPages;
  }

  handleDotState() {
    this.dotsWrapper?.querySelectorAll('[data-slide]').forEach((button) => {
      const active = Number(button.dataset.slide) === this.currentPage;
      button.classList.toggle('slider-counter__link--active', active);
      button.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  initializeArrows() {
    this.prevButton?.removeEventListener('click', this.onPrevious);
    this.nextButton?.removeEventListener('click', this.onNext);
    if (this.dataset.arrowsSelector === 'custom' && this.dataset.prevSelector && this.dataset.nextSelector) {
      this.prevButton = document.querySelector(this.dataset.prevSelector);
      this.nextButton = document.querySelector(this.dataset.nextSelector);
    } else {
      this.prevButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');
    }
    if (!this.prevButton || !this.nextButton) return;
    this.prevButton.classList.remove('hidden');
    this.nextButton.classList.remove('hidden');
    this.prevButton.addEventListener('click', this.onPrevious);
    this.nextButton.addEventListener('click', this.onNext);
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    if (this.started) this.refresh();
  }

  loadDeferredMedia() {
    if (!this.isConnected) return;
    this.querySelectorAll('template[data-slider-media]').forEach((template) => {
      template.replaceWith(template.content.cloneNode(true));
    });
  }

  setSlidePosition(position) { this.slider.scrollTo({ left: position }); }
  play() {
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.onNext, this.autoplaySpeed);
  }
  goToNextSlide() { this.goToSlide(this.currentPage === this.totalPages && this.loop ? 1 : this.currentPage + 1); }
  goToPreviousSlide() { this.goToSlide(this.currentPage === 1 && this.loop ? this.totalPages : this.currentPage - 1); }
  goToSlide(page) {
    this.loadDeferredMedia();
    this.slideScrollPosition = (page - 1) * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }
}
customElements.define('dbtfy-slider-component', DbtfySliderComponent);
